import { useEffect, useRef, useState } from 'react';
import { fiscalDate, formatMoney } from '@makila/fiscal';
import { RX_VALID_DAYS, type Prescription } from '@makila/pos-core';
import { X } from 'lucide-react';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { Operator } from '../screens/Lock';
import type { Product } from '../screens/Sale';
import { ManagerApproval } from './ManagerApproval';

function Shell(p: { title: string; onClose: () => void; children: React.ReactNode }) {
  const dlg = useRef<HTMLDialogElement>(null);
  useEffect(() => { dlg.current?.showModal(); }, []);
  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className="m-auto w-[min(600px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">{p.title}</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="max-h-[75vh] overflow-y-auto p-5">{p.children}</div>
    </dialog>
  );
}

const input = 'h-12 w-full rounded-xl border border-base bg-surface-2 px-4';

/**
 * Receita (Decreto 16/2023, art. 67–70): pedida antes de pagar quando há
 * medicamentos sujeitos a receita. O doente é opcional (minimização dos dados),
 * salvo no controlo especial, que exige também a cédula do prescritor e o
 * farmacêutico responsável.
 */
export function PrescriptionDialog(p: { store: IdbTerminalStore; operator: Operator; controlled: boolean; onClose: () => void; onDone: (rx: Prescription) => void }) {
  const [f, setF] = useState({ number: '', date: fiscalDate(), prescriber: '', prescriberLicence: '', patient: '', patientId: '', vias: '1' });
  const [chronic, setChronic] = useState(false);
  const old = Math.round((Date.parse(`${fiscalDate()}T00:00:00Z`) - Date.parse(`${f.date || fiscalDate()}T00:00:00Z`)) / 86_400_000) > RX_VALID_DAYS;
  const needPharmacist = p.controlled || (old && chronic);
  const [pharmacist, setPharmacist] = useState<Operator | null>(p.operator.role === 'manager' ? p.operator : null);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => { setF({ ...f, [k]: e.target.value }); setErr(null); };
  const done = () => {
    if (!f.number.trim() || !f.prescriber.trim()) return setErr('Indique o número da receita e o prescritor');
    if (!f.date || f.date > fiscalDate()) return setErr('Data da receita inválida');
    if (p.controlled && (!f.patient.trim() || !f.patientId.trim() || !f.prescriberLicence.trim())) return setErr('Controlo especial: doente (nome e documento) e cédula do prescritor');
    if (old && !chronic) return setErr(`Receita com mais de ${RX_VALID_DAYS} dias: só para doente crónico (marque e chame o farmacêutico)`);
    if (needPharmacist && !pharmacist) return setErr('Falta a autorização do farmacêutico responsável');
    p.onDone({
      number: f.number.trim(), date: f.date, prescriber: f.prescriber.trim(), prescriberLicence: f.prescriberLicence.trim() || null,
      patient: f.patient.trim() || null, patientId: f.patientId.trim() || null, vias: Math.max(1, Number(f.vias) || 1), pharmacistId: needPharmacist ? pharmacist!.id : null, ...(old && chronic ? { chronic: true } : {}),
    });
  };
  return (
    <Shell title={p.controlled ? 'Receita — controlo especial' : 'Receita médica'} onClose={p.onClose}>
      <div className="grid gap-3 sm:grid-cols-2">
        <input autoFocus aria-label="Número da receita" placeholder="N.º da receita" value={f.number} onChange={set('number')} className={input} />
        <input aria-label="Data da receita" type="date" value={f.date} onChange={set('date')} className={input} />
        <input aria-label="Prescritor" placeholder="Prescritor (médico)" value={f.prescriber} onChange={set('prescriber')} className={input} />
        <input aria-label="Cédula do prescritor" placeholder={`Cédula / ordem${p.controlled ? '' : ' (opcional)'}`} value={f.prescriberLicence} onChange={set('prescriberLicence')} className={input} />
        <input aria-label="Doente" placeholder={`Doente${p.controlled ? '' : ' (opcional)'}`} value={f.patient} onChange={set('patient')} className={input} />
        <input aria-label="Documento do doente" placeholder={`Documento do doente${p.controlled ? '' : ' (opcional)'}`} value={f.patientId} onChange={set('patientId')} className={input} />
        <label className="flex items-center gap-2 text-sm">Vias da receita <input aria-label="Vias da receita" inputMode="numeric" value={f.vias} onChange={set('vias')} className="h-12 w-16 rounded-xl border border-base bg-surface-2 text-center" /></label>
      </div>
      {old && <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={chronic} onChange={(e) => setChronic(e.target.checked)} /> Doente crónico (receita com mais de {RX_VALID_DAYS} dias)</label>}
      {needPharmacist && !pharmacist && (
        <div className="mt-4"><ManagerApproval store={p.store} operator={p.operator} why="Autorização do farmacêutico responsável." onApproved={setPharmacist} /></div>
      )}
      {needPharmacist && pharmacist && <p className="mt-3 text-sm text-muted">Farmacêutico responsável: {pharmacist.name}</p>}
      {err && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{err}</p>}
      <button onClick={done} className="mt-4 h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white">Continuar para o pagamento</button>
    </Shell>
  );
}

/**
 * Substitutos pelo princípio activo (genéricos). Num medicamento receitado, só
 * com o consentimento do prescritor (art. 14 n.º 2): sem ele, não se substitui.
 */
export function SubstituteDialog(p: { line: { product: Product; prescribed: boolean }; options: Product[]; onClose: () => void; onPick: (prod: Product, prescriberConsent: boolean) => void }) {
  const [pick, setPick] = useState<Product | null>(null);
  const [refused, setRefused] = useState(false);
  return (
    <Shell title={`Substitutos — ${p.line.product.activeIngredient}`} onClose={p.onClose}>
      {!pick ? (
        <ul className="divide-y divide-[var(--border)]">
          {p.options.map((o) => (
            <li key={o.id}><button onClick={() => (p.line.prescribed ? setPick(o) : p.onPick(o, false))} className="flex h-14 w-full items-center justify-between px-2 text-left hover:bg-surface-2">
              <span>{o.name}{o.requiresPrescription && <span className="ml-2 text-xs text-brand-700">receita</span>}</span><span className="num font-semibold">{formatMoney(o.unitPrice)} MT</span>
            </button></li>
          ))}
        </ul>
      ) : (
        <div>
          <p>O {p.line.product.name} foi <b>receitado</b>. O prescritor consentiu a substituição por {pick.name}?</p>
          {refused && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">Sem o consentimento do prescritor não se substitui um medicamento receitado (art. 14 do Decreto 16/2023).</p>}
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button onClick={() => setRefused(true)} className="h-12 rounded-xl border border-base font-semibold">Não</button>
            <button onClick={() => p.onPick(pick, true)} className="h-12 rounded-xl bg-brand-700 font-bold text-white">Sim, consentiu</button>
          </div>
        </div>
      )}
    </Shell>
  );
}
